import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Typography,
    useTheme,
} from '@mui/material'
import FactCheckIcon from '@mui/icons-material/FactCheck'

import { AppRoutes, resolveRoute } from '../../helpers/Routes'
import { getShortFormDate } from '../../helpers/stringHelpers'
import {
    PropertyInspectionComplete,
    PropertyInspectionUpcoming,
    TenantInspectionHubPropertyInspections,
} from '../../types/inspection'
import { LifecycleSection, InspectionEmptyState } from './InspectionSectionCard'
import { InspectionSummaryCard } from './InspectionSummaryCard'
import {
    flattenFlaggedItemsFromDetails,
    countAreas,
    getLatestCompletedDate,
    flaggedItemsToPreviewItems,
    isOverdue,
} from './inspectionUtils'
import { InspectionChipStatus } from './InspectionStatusChip'

const COMPLETE_PREVIEW_LIMIT = 3

interface PropertyInspectionsSectionProps {
    propertyInspections: TenantInspectionHubPropertyInspections
    defaultExpanded?: boolean
}

const formatDateRange = (startDate: string, endDate: string) => {
    return `${getShortFormDate(startDate)} – ${getShortFormDate(endDate)}`
}

const formatAreas = (areas: { area_label: string; unit_name: string }[]) => {
    return areas.map((a) => `${a.unit_name} — ${a.area_label}`).join(', ')
}

interface UpcomingCardProps {
    entry: PropertyInspectionUpcoming
}

const UpcomingCard: React.FC<UpcomingCardProps> = ({ entry }) => {
    const overdue = isOverdue(entry.end_date)

    return (
        <Card
            sx={{
                bgcolor: 'rgba(255,255,255,0.04)',
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid rgba(255,255,255,0.1)',
            }}
        >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 0.5,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}
                    >
                        {entry.name}
                    </Typography>
                    <Chip
                        label={overdue ? 'Overdue' : 'Upcoming'}
                        size="small"
                        color={overdue ? 'error' : 'warning'}
                        sx={{ flexShrink: 0, fontWeight: 600 }}
                    />
                </Box>
                <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.5)', mb: 0.5 }}
                >
                    {entry.inspection_type_name}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.5)', mb: 0.5 }}
                >
                    {formatDateRange(entry.start_date, entry.end_date)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    Areas: {formatAreas(entry.areas)}
                </Typography>
            </CardContent>
        </Card>
    )
}

interface CompleteCardProps {
    entry: PropertyInspectionComplete
    onViewResults: (entry: PropertyInspectionComplete) => void
}

const CompleteCard: React.FC<CompleteCardProps> = ({ entry, onViewResults }) => {
    const previewItems = flaggedItemsToPreviewItems(
        flattenFlaggedItemsFromDetails(entry.detail),
    )
    const areaCount = countAreas(entry.detail)
    const completedDate =
        entry.completed_date ?? getLatestCompletedDate(entry.detail)

    return (
        <InspectionSummaryCard
            icon={<FactCheckIcon sx={{ fontSize: 22 }} />}
            title={entry.name}
            status="complete"
            completedDate={completedDate}
            areaCount={areaCount}
            previewItems={previewItems}
            previewVariant="flagged"
            onViewResults={() => onViewResults(entry)}
        />
    )
}

export const PropertyInspectionsSection: React.FC<
    PropertyInspectionsSectionProps
> = ({ propertyInspections, defaultExpanded = false }) => {
    const navigate = useNavigate()
    const theme = useTheme()
    const { upcoming, complete } = propertyInspections
    const isEmpty = upcoming.length === 0 && complete.length === 0
    const [showAllComplete, setShowAllComplete] = useState(false)

    const hasOverdue = upcoming.some((u) => isOverdue(u.end_date))
    const visibleComplete = showAllComplete
        ? complete
        : complete.slice(0, COMPLETE_PREVIEW_LIMIT)
    const hiddenCompleteCount = complete.length - COMPLETE_PREVIEW_LIMIT

    const handleViewResults = (entry: PropertyInspectionComplete) => {
        navigate(
            resolveRoute(
                AppRoutes.inspectionPropertyDetail,
                ':inspectionId',
                entry.inspection_id,
            ),
            { state: { propertyInspection: entry } },
        )
    }

    const getSectionStatus = (): InspectionChipStatus | undefined => {
        if (hasOverdue) return 'overdue'
        if (upcoming.length > 0) return 'upcoming'
        if (complete.length > 0) return 'complete'
        return undefined
    }

    const getSummary = () => {
        if (isEmpty) return 'No inspections shared yet'
        const parts: string[] = []
        if (complete.length > 0) {
            parts.push(`${complete.length} complete`)
        }
        if (upcoming.length > 0) {
            parts.push(`${upcoming.length} upcoming`)
        }
        return parts.join(' · ')
    }

    return (
        <LifecycleSection
            sectionNumber={2}
            title="Property Inspections"
            icon={<FactCheckIcon />}
            status={getSectionStatus()}
            summary={getSummary()}
            defaultExpanded={defaultExpanded}
        >
            {isEmpty && (
                <InspectionEmptyState message="No property inspections shared yet" />
            )}

            {complete.length > 0 && (
                <Box sx={{ mb: upcoming.length > 0 ? 2 : 0 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgba(255,255,255,0.6)',
                            fontWeight: 600,
                            mb: 1,
                            textTransform: 'uppercase',
                            fontSize: '0.75rem',
                            letterSpacing: 0.5,
                        }}
                    >
                        Complete
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {visibleComplete.map((entry) => (
                            <CompleteCard
                                key={`complete-${entry.inspection_id}`}
                                entry={entry}
                                onViewResults={handleViewResults}
                            />
                        ))}
                    </Box>
                    {!showAllComplete && hiddenCompleteCount > 0 && (
                        <Button
                            fullWidth
                            onClick={() => setShowAllComplete(true)}
                            sx={{
                                mt: 1.5,
                                textTransform: 'none',
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                            }}
                        >
                            Show all ({complete.length})
                        </Button>
                    )}
                </Box>
            )}

            {upcoming.length > 0 && (
                <Box>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgba(255,255,255,0.6)',
                            fontWeight: 600,
                            mb: 1,
                            textTransform: 'uppercase',
                            fontSize: '0.75rem',
                            letterSpacing: 0.5,
                        }}
                    >
                        Upcoming
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            maxHeight: upcoming.length > 3 ? 360 : undefined,
                            overflowY: upcoming.length > 3 ? 'auto' : undefined,
                            pr: upcoming.length > 3 ? 0.5 : 0,
                        }}
                    >
                        {upcoming.map((entry) => (
                            <UpcomingCard
                                key={`upcoming-${entry.inspection_id}`}
                                entry={entry}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </LifecycleSection>
    )
}
